import { useState, useEffect } from 'react';
import {
    Box, Typography, Grid, Card, CardContent, CircularProgress, Alert, TextField,
    Skeleton, Snackbar, Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import BusinessIcon from '@mui/icons-material/Business';
import CategoryIcon from '@mui/icons-material/Category';
import InventoryIcon from '@mui/icons-material/Inventory';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from '../../utils/endpoints';
import ContactPageIcon from '@mui/icons-material/ContactPage';

const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.6,
            ease: 'easeOut',
            when: 'beforeChildren',
            staggerChildren: 0.15,
        },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 80, scale: 0.85 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
            duration: 0.5,
            ease: 'easeOut',
        },
    },
    hover: {
        scale: 1.01,
        boxShadow: '0 8px 20px rgba(0,0,0,0.25)',
        transition: {
            duration: 0.2,
            ease: 'easeOut',
        },
    },
    tap: {
        scale: 1.05,
        transition: {
            duration: 0.2,
            ease: 'easeOut',
        },
    },
};

const numberVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut', delay: 0.2 } },
};

const cardStyles = {
    bgcolor: '#f7f7f7',
    boxShadow: 4,
    borderRadius: 3,
    border: '1px solid #a5d6a7',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    p: 2,
    width: "150px",
    height: "160px",
    overflow: 'hidden',
    cursor: 'pointer',
};

const Dashboard = () => {
    const [data, setData] = useState({
        brands: 0,
        categories: 0,
        products: 0,
        sliders: 0,
        images: 0,
        brandForms: 0,
        contacts: 0,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [searchTerm, setSearchTerm] = useState('');

    const navigate = useNavigate();

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('authToken');
        if (!token) {
            setError('No authorization token found in localStorage');
            setSnackbar({ open: true, message: 'Authorization token missing. Please log in.', severity: 'error' });
            setLoading(false);
            return;
        }

        const endpoints = [
            { name: 'brands', url: `${BASE_URL}/brands${searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : ''}` },
            { name: 'categories', url: `${BASE_URL}/categories${searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : ''}` },
            { name: 'products', url: `${BASE_URL}/products${searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : ''}` },
            { name: 'sliders', url: `${BASE_URL}/sliders${searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : ''}` },
            { name: 'brandForms', url: `${BASE_URL}/brand-forms${searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : ''}` },
            { name: 'images', url: `${BASE_URL}/images${searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : ''}` },
            { name: 'contacts', url: `${BASE_URL}/contacts${searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : ''}` },
        ];

        try {
            const results = await Promise.all(
                endpoints.map(async (endpoint) => {
                    const response = await fetch(endpoint.url, {
                        method: 'GET',
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                    });
                    if (!response.ok) {
                        const errorBody = await response.text();
                        throw new Error(`Failed to fetch ${endpoint.name}: ${response.status} - ${errorBody}`);
                    }
                    const result = await response.json();
                    const count = Array.isArray(result)
                        ? result.length
                        : result[endpoint.name]
                            ? result[endpoint.name].length
                            : Object.keys(result).length;
                    return { name: endpoint.name, count };
                })
            );

            const newData = results.reduce((acc, { name, count }) => ({
                ...acc,
                [name]: count || 0,
            }), {});
            setData(newData);
            setSnackbar({ open: true, message: 'Dashboard data refreshed!', severity: 'success' });
        } catch (err) {
            setError(err.message);
            setSnackbar({ open: true, message: `Error fetching data: ${err.message}`, severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [searchTerm]);

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar({ ...snackbar, open: false });
    };


    const routeMap = {
        Brands: '/brand',
        Categories: '/getcategories',
        Products: '/getproduct',
        Sliders: '/getslider',
        'Brand Forms': '/brandforms',
        Images: '/gallery',
        Contacts: '/contact',
    };

    const handleCardClick = (label) => {
        const route = routeMap[label];
        if (route) {
            navigate(route);
        }
    };

    const renderSkeleton = () => (
        <Grid container spacing={2}>
            {[...Array(6)].map((_, index) => (
                <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    key={`skeleton-${index}`}
                    component={motion.div}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                >
                    <Card sx={cardStyles}>
                        <CardContent sx={{ textAlign: 'center', p: 2, width: '100%' }}>
                            <Skeleton
                                variant="circular"
                                width={32}
                                height={32}
                                sx={{ mb: 1, mx: 'auto', bgcolor: 'grey.300' }}
                            />
                            <Skeleton
                                variant="text"
                                width="70%"
                                height={30}
                                sx={{ mb: 1.5, mx: 'auto', bgcolor: 'grey.300' }}
                            />
                            <Skeleton
                                variant="text"
                                width="50%"
                                height={40}
                                sx={{ mx: 'auto', bgcolor: 'grey.300' }}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );

    if (error && Object.values(data).every(count => count === 0) && !loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: '#f8f9fa',
                    borderRadius: 2,
                    p: 4,
                    boxShadow: 3,
                }}
            >
                <Alert severity="error" sx={{ mb: 3, border: '1px solid #d32f2f', width: 'auto' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>Oops! Something went wrong.</Typography>
                    <Typography variant="body1">{error}</Typography>
                </Alert>
                <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={fetchData}
                    sx={{
                        bgcolor: '#2e7d32',
                        '&:hover': { bgcolor: '#1b5e20' },
                        color: '#fff',
                    }}
                >
                    Try Again
                </Button>
            </Box>
        );
    }

    const cardData = [
        { label: 'Brands', key: 'brands', icon: <BusinessIcon sx={{ fontSize: 42, color: '#2e7d32' }} /> },
        { label: 'Categories', key: 'categories', icon: <CategoryIcon sx={{ fontSize: 42, color: '#2e7d32' }} /> },
        { label: 'Products', key: 'products', icon: <InventoryIcon sx={{ fontSize: 42, color: '#2e7d32' }} /> },
        { label: 'Sliders', key: 'sliders', icon: <SlideshowIcon sx={{ fontSize: 42, color: '#2e7d32' }} /> },
        { label: 'Brand Forms', key: 'brandForms', icon: <DescriptionIcon sx={{ fontSize: 42, color: '#2e7d32' }} /> },
        { label: 'Images', key: 'images', icon: <ImageIcon sx={{ fontSize: 42, color: '#2e7d32' }} /> },
        { label: 'Contacts', key: 'contacts', icon: <ContactPageIcon sx={{ fontSize: 42, color: '#2e7d32' }} /> },
    ];

    return (
        <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            style={{
                marginTop: '24px',
                borderRadius: '12px',
                padding: '32px',
                backgroundColor: '#ffffff',
                boxShadow: '0px 6px 18px rgba(0,0,0,0.08)',
                fontFamily: '"Inter", sans-serif',
                minHeight: 'calc(100vh - 48px)',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <motion.div initial={{ opacity: 0, y: -30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#2e7d32', mb: 0.5 }}>
                    Dashboard Overview
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Quick insights into your platform's data.
                </Typography>
            </motion.div>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, gap: 2 }}>
                <TextField
                    placeholder="Search all data..."
                    size="small"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ marginRight: 1, color: '#616161' }} />,
                        sx: {
                            borderRadius: '8px',
                            '& fieldset': { borderColor: '#e0e0e0 !important' },
                            '&:hover fieldset': { borderColor: '#a5d6a7 !important' },
                            '&.Mui-focused fieldset': { borderColor: '#2e7d32 !important', borderWidth: '2px !important' },
                        },
                    }}
                    sx={{ flexGrow: 1, maxWidth: '300px' }}
                />
                <Button
                    variant="outlined"
                    onClick={fetchData}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                    sx={{
                        color: '#2e7d32',
                        borderColor: '#2e7d32',
                        '&:hover': {
                            bgcolor: '#e8f5e9',
                            borderColor: '#1b5e20',
                        },
                        borderRadius: '8px',
                        py: 1,
                        px: 2,
                    }}
                >
                    {loading ? 'Refreshing...' : 'Refresh Data'}
                </Button>
            </Box>

            <Box sx={{ flexGrow: 1 }}>
                <AnimatePresence mode="wait">
                    {loading && Object.values(data).every(count => count === 0) ? (
                        renderSkeleton()
                    ) : (
                        <motion.div
                            key="data-cards"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <Grid container spacing={2}>
                                {cardData.map(({ label, key, icon }) => (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={4}
                                        key={key}
                                        component={motion.div}
                                        variants={itemVariants}
                                        initial="hidden"
                                        animate="visible"
                                        whileHover="hover"
                                        whileTap="tap"
                                        layout
                                    >
                                        <Card sx={cardStyles}>
                                            <CardContent
                                                sx={{
                                                    textAlign: 'center', width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                }}
                                                onClick={() => handleCardClick(label)}
                                            >
                                                {icon}
                                                <Box>
                                                    <motion.div variants={numberVariants}>
                                                        <Typography
                                                            variant="h3"
                                                            sx={{
                                                                color: '#2e7d32',
                                                                fontWeight: 800,
                                                                lineHeight: 1,
                                                            }}
                                                        >
                                                            {data[key]}
                                                        </Typography>
                                                    </motion.div>
                                                    <motion.div variants={numberVariants}>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                color: '#2e7d32',
                                                                mb: 1,
                                                                mt: 1,
                                                                fontWeight: 600,
                                                                letterSpacing: '0.02em',
                                                            }}
                                                        >
                                                            {label}
                                                        </Typography>
                                                    </motion.div>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%', borderRadius: 2, boxShadow: 8 }}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </motion.div>
    );
};

export default Dashboard;